import React, { useEffect, useState } from "react";
import DeleteDataModel from "./DeleteDataModel";
import { LIcon } from "assests/GmsIcons";
import { TConnector, TField, TIndexState, TProductCategory, TSubmodel, TTransformation, TUpsertISRProjectDataModelsInput,} from "../model";
import { CALCULATED, PRODUCT_CATEGORY, TRANSFORMATION,} from "Pages/GMS/ISR/ISRConstants";
import Status from "Components/DesignSystem/Status/Status";
import Typography from "Components/DesignSystem/Typography/Typography";
import { FOCUS_RING_INSET } from "Components/DesignSystem/focusRing";
import { EXPLORER_ROW, EXPLORER_ROW_STATE, EXPLORER_SELECT_BTN } from "./explorerRowChrome";

type TProductCategoryItemProps = {
  id: string;
  field?: TProductCategory;
  fieldName: string;
  fieldType: string;
  classColor?: string;
  isSelected: boolean;
  onSelect: () => void;
  indexInfo: TIndexState;
  isSubSelected?: boolean;
  onSubSelect?: () => void;
  showCalculatedField?: boolean;
  onDelete: (fieldName: string|null|undefined) => void;
  userProjectDataModels: TUpsertISRProjectDataModelsInput;
  setIndex?: React.Dispatch<React.SetStateAction<TIndexState>>;
  setSubSelectedItem: React.Dispatch<React.SetStateAction<TTransformation | null>>;
  selectedItem: | TField | TSubmodel | TProductCategory | TConnector | TTransformation | null;
  setUserProjectDataModels?: React.Dispatch<React.SetStateAction<TUpsertISRProjectDataModelsInput>>;
  onSubDelete: (categoryId: string, transformationId:string) => void;
};

/**
 * A category row in the Model Explorer, plus its transformation sub-rows.
 *
 * Restyled onto the design system 2026-08-30 — `okf/isr/e2e.md` § "The expandable
 * list", and see `explorerRowChrome.ts` for what the shared row classes changed and
 * why. Everything above the `return` is untouched: the type-sync effect, the
 * `TransformationIndex` mirror and `onSubSelect`'s `setIndex` updater are behaviour.
 */
const ProductCategoryItem = ({
  id,
  field,
  fieldName,
  fieldType,
  onDelete,
  isSelected,
  onSelect,
  selectedItem,
  userProjectDataModels,
  indexInfo,
  showCalculatedField,
  classColor,
  setSubSelectedItem,
  setIndex,
  setUserProjectDataModels,
  onSubDelete
}: TProductCategoryItemProps) => {
  const [name, setName] = useState<string>("");
  const [subSelected, setSubSelected] = useState<any>(indexInfo?.TransformationIndex);
  const handleContextMenuOptionClick = () => {
    onDelete(field?.id);
  };
  const handleContextMenuTransformationOptionClick = (transformationId:string) => {
    onSubDelete(field?.id as string, transformationId);
  };
  useEffect(() => {
    if (
      selectedItem &&
      "belongsToDataModel" in selectedItem &&
      selectedItem?.belongsToDataModel
    ) {
      setName(
        userProjectDataModels?.project.dataModels[
          indexInfo?.dataModelIndex ?? -1
        ]?.data.fields[indexInfo?.fieldIndex ?? -1].type ?? ""
      );
    } else {
      setName(
        userProjectDataModels?.project.fields[
          indexInfo?.fieldOutsideDataModelIndex ?? -1
        ]?.data?.type ?? ""
      );
    }
  }, [selectedItem]);

  useEffect(()=>{
    setSubSelected(indexInfo?.TransformationIndex)
  }, [indexInfo?.TransformationIndex])

  const onSubSelect = (index: number, transformation: TTransformation) => {
    setSubSelected(index);
    if (setIndex)
    setIndex((prev)=>{
      let newState = {...prev,
        TransformationIndex: index}
        return newState
        }
        );
    setSubSelectedItem(transformation);
  };

  /*
    Unchanged behaviour, moved out of the wrapper's `onClick` and onto the select
    button: the main row selects itself AND clears any transformation sub-selection.
  */
  const handleSelectRow = () => {
    onSelect();
    setSubSelected(null);
    setSubSelectedItem(null);
  };

  return (
    <div id={id}>
      <div
        id={`${id}-main`}
        data-testid={id}
        className={`${EXPLORER_ROW} ${EXPLORER_ROW_STATE(isSelected)}`}
      >
        <button
          type="button"
          id={`${id}-select-btn`}
          data-testid={`${id}-select-btn`}
          onClick={handleSelectRow}
          aria-current={isSelected ? 'true' : undefined}
          className={`${EXPLORER_SELECT_BTN} ${FOCUS_RING_INSET}`}
        >
          <div id={`${id}-content`} className="tw-flex tw-justify-start tw-items-stretch tw-w-full">
            {classColor ? (
              <div
                id={`${id}-color-bar`}
                style={{ backgroundColor: classColor }}
                className="tw-w-[5px] tw-shrink-0 tw-self-stretch tw-rounded-tl-[3px] tw-rounded-bl-[3px]"
              />
            ) : null}
            <div id={`${id}-info`} className="tw-px-3 tw-py-[5px] tw-min-w-0 tw-text-left">
              <Typography
                as="div"
                variant={isSelected ? 'sub-title-label' : 'sub-body-supporting'}
                id={`${id}-name`}
                data-testid={`${id}-name`}
                className="tw-break-words"
              >
                {fieldName}
              </Typography>
              <div id={`${id}-details`} className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                <Typography
                  as="div"
                  variant="caption-metadata"
                  id={`${id}-type`}
                  data-testid={`${id}-type`}
                  className={isSelected ? '' : 'tw-text-ink-tertiary'}
                >
                  {fieldType}
                </Typography>
                {showCalculatedField && showCalculatedField ? (
                  <Status
                    tone="warning"
                    label={CALCULATED}
                    id={`${id}-calculated-badge`}
                    data-testid={`${id}-calculated-badge`}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </button>
        {/* `onTriggerClick` reproduces the bubbling that used to select this row when
            its ⋮ was opened — see the prop's note in `DeleteDataModel`. */}
        <div id={`${id}-delete-menu`} className="tw-flex tw-items-center tw-pe-2">
          <DeleteDataModel
            id={id}
            onContextMenuOptionClick={handleContextMenuOptionClick}
            onTriggerClick={handleSelectRow}
          />
        </div>
      </div>

      {isSelected &&
        [PRODUCT_CATEGORY, TRANSFORMATION].includes(fieldType) &&
        field?.data?.transformations &&
        field.data.transformations.map(
          (transformation: TTransformation, index: number) => (
            <div
              id={`${id}-transformation-${index}`}
              data-testid={`${id}-transformation-${index}`}
              key={index}
              className="tw-flex tw-justify-start tw-w-auto tw-ms-2"
            >
              <LIcon />
              <div
                id={`${id}-transformation-content-${index}`}
                /*
                  Was `bg-white` here while `FieldItem`'s fraction sub-row — the same
                  affordance one component over — was `#F4F5F7`. Both are
                  `surface-sunken` now.
                */
                className={`tw-mt-2 tw-rounded-ctl tw-bg-surface-sunken tw-box-border tw-w-full tw-flex tw-flex-row tw-items-stretch tw-justify-between tw-gap-3 tw-border tw-border-solid motion-safe:tw-transition-colors ${
                  subSelected === index
                    ? "tw-border-accent tw-bg-accent-tint tw-text-accent"
                    : "tw-border-edge tw-text-ink hover:tw-border-edge-strong"
                }`}
              >
                <button
                  type="button"
                  id={`${id}-transformation-select-btn-${index}`}
                  data-testid={`${id}-transformation-select-btn-${index}`}
                  onClick={() => {
                    onSubSelect(index, transformation);
                  }}
                  aria-current={subSelected === index ? 'true' : undefined}
                  className={`${EXPLORER_SELECT_BTN} ${FOCUS_RING_INSET}`}
                >
                  <div id={`${id}-transformation-info-${index}`} className="tw-px-3 tw-py-[5px] tw-min-w-0 tw-text-left">
                    {/* #6D788C (4.45:1) and #97A0AF (2.42:1 on this ground) were both
                        under WCAG AA — see the same note in `FieldItem`. */}
                    <Typography
                      as="div"
                      variant="sub-body-supporting"
                      id={`${id}-transformation-name-${index}`}
                      className="tw-italic tw-break-words"
                    >
                      {transformation.name}
                    </Typography>
                    <Typography
                      as="div"
                      variant="caption-metadata"
                      id={`${id}-transformation-type-${index}`}
                      className={`tw-italic ${subSelected === index ? '' : 'tw-text-ink-secondary'}`}
                    >
                      {transformation.type}
                    </Typography>
                  </div>
                </button>
                <div id={`${id}-transformation-delete-${index}`} className="tw-flex tw-items-center tw-pe-2">
                  <DeleteDataModel
                    id={`${id}-transformation-${index}`}
                    onContextMenuOptionClick={()=>handleContextMenuTransformationOptionClick(transformation?.id as string)}
                    onTriggerClick={() => onSubSelect(index, transformation)}
                  />
                </div>
              </div>
            </div>
          )
        )}
    </div>
  );
};

export default ProductCategoryItem;
